import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, RotateCcw, ScanLine, Package,
  Printer, AlertTriangle, CheckCircle2, History,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('session_token')}` };
}

// --------------------------------------------------------------------
// Tab 1 — Reverse a combo
// --------------------------------------------------------------------
function ReverseTab() {
  const [comboInput, setComboInput] = useState('');
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handlePreview = async () => {
    const c = comboInput.trim();
    if (!c) return;
    setBusy(true);
    setPreview(null);
    setLastResult(null);
    try {
      const r = await axios.post(
        `${API}/api/combo-v2/reversal/preview`,
        { combo_usn: c },
        { headers: authHeaders() }
      );
      setPreview(r.data);
      toast.success('Combo loaded — review components below');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load combo');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview || !reason.trim() || reason.trim().length < 3) {
      toast.error('Reason is required (min 3 chars)');
      return;
    }
    setConfirming(true);
    try {
      const r = await axios.post(
        `${API}/api/combo-v2/reversal/confirm`,
        {
          combo_usn: preview.combo.combo_usn,
          reason: reason.trim(),
        },
        { headers: authHeaders() }
      );
      toast.success(`Reversal ${r.data.reversal_id} complete (${r.data.components_queued} pcs queued)`);
      setLastResult(r.data);
      setPreview(null);
      setComboInput('');
      setReason('');
      inputRef.current?.focus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reverse combo');
    } finally {
      setConfirming(false);
    }
  };

  const handlePrintLabels = () => {
    if (!preview) return;
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) { toast.error('Pop-up blocked — please allow pop-ups for this site'); return; }
    const rows = preview.components.flatMap(c =>
      c.usns.map(u => ({
        usn: u.usn_code,
        bundle: u.bundle_code,
        sku: c.component_barcode,
        component: c.component_name,
        design: c.design_name,
        shade: c.shade,
        size: c.size,
        desc: c.description,
        mrp: c.mrp,
      }))
    );
    win.document.write(`
      <html><head><title>V2 Reversal Labels — ${preview.combo.combo_usn}</title>
      <style>
        body { font-family: monospace; padding: 12px; }
        .label { border: 2px dashed #444; padding: 12px; margin-bottom: 12px;
                 width: 320px; page-break-after: always; }
        .lab { font-size: 18px; font-weight: bold; }
        .small { font-size: 11px; color: #555; }
        .usn { font-size: 22px; font-weight: bold; letter-spacing: 1px; }
        .qr { font-size: 10px; word-break: break-all; }
        h2 { margin: 0 0 6px 0; }
        @media print { .no-print { display: none; } }
      </style></head><body>
      <div class="no-print" style="margin-bottom:8px;">
        <button onclick="window.print()">Print</button>
        <span style="margin-left:8px;">Combo: <b>${preview.combo.combo_usn}</b> · Reversal preview · ${rows.length} component label(s)</span>
      </div>
      ${rows.map(r => `
        <div class="label">
          <h2>${r.design || ''} ${r.shade || ''}</h2>
          <div class="small">${r.desc || ''} ${r.size ? '· ' + r.size : ''}</div>
          <div class="lab" style="margin-top:6px;">SKU: ${r.sku}</div>
          <div class="usn" style="margin-top:6px;">${r.usn}</div>
          <div class="small" style="margin-top:6px;">Bundle: ${r.bundle || '—'}</div>
          ${r.mrp ? `<div style="margin-top:6px;">MRP: Rs.${Number(r.mrp).toFixed(2)}</div>` : ''}
          <div class="qr" style="margin-top:6px;">QR: ${r.usn}</div>
        </div>
      `).join('')}
      </body></html>
    `);
    win.document.close();
  };

  return (
    <div className="space-y-4">
      <Card className="p-4" data-testid="combo-v2-reversal-scan-card">
        <div className="text-sm font-semibold text-stone-700 mb-2">
          Step 1 — Scan / paste the assembled Combo USN
        </div>
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={comboInput}
            onChange={(e) => setComboInput(e.target.value)}
            placeholder="CMB-123456-..."
            onKeyDown={(e) => e.key === 'Enter' && handlePreview()}
            disabled={busy}
            className="font-mono"
            data-testid="combo-v2-reversal-input"
          />
          <Button
            onClick={handlePreview}
            disabled={busy || !comboInput.trim()}
            data-testid="combo-v2-reversal-load-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanLine className="h-4 w-4 mr-1" />}
            Load Components
          </Button>
        </div>
      </Card>

      {lastResult && (
        <Alert className="bg-emerald-50 border-emerald-200" data-testid="combo-v2-reversal-success">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <AlertTitle>Reversal Complete</AlertTitle>
          <AlertDescription>
            <div>Reversal ID: <b>{lastResult.reversal_id}</b></div>
            <div>Combo: <b>{lastResult.combo_usn}</b> — {lastResult.components_queued} component pieces pushed to the loose-USN queue.</div>
          </AlertDescription>
        </Alert>
      )}

      {preview && (
        <Card className="p-4" data-testid="combo-v2-reversal-preview-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-stone-900 flex items-center gap-2">
                <Package className="h-4 w-4" />
                {preview.combo.combo_name || preview.combo.combo_barcode}
              </h3>
              <div className="text-xs text-stone-500 mt-1">
                Combo USN: <span className="font-mono font-semibold">{preview.combo.combo_usn}</span> ·
                Status: <Badge variant="outline" className="ml-1">{preview.combo.status}</Badge>
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Session: <span className="font-mono">{preview.combo.session_id}</span> ·
                Position: #{preview.combo.session_assembled_index + 1}
              </div>
            </div>
            <Button variant="outline" onClick={handlePrintLabels} data-testid="combo-v2-reversal-print-btn">
              <Printer className="h-4 w-4 mr-1" /> Print Component Labels
            </Button>
          </div>

          <div className="space-y-3">
            {preview.components.map((comp, idx) => (
              <div
                key={idx}
                className="border border-stone-200 rounded p-3 bg-stone-50"
                data-testid={`combo-v2-reversal-component-${idx}`}
              >
                <div className="text-sm font-semibold text-stone-800">
                  {comp.component_name}
                  <span className="text-stone-500 font-normal ml-2 text-xs">
                    SKU {comp.component_barcode} · {comp.design_name} {comp.shade} {comp.size && '· ' + comp.size}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {comp.usns.map(u => (
                    <div
                      key={u.usn_code}
                      className="flex items-center justify-between bg-white border border-stone-200 rounded px-3 py-2"
                    >
                      <div>
                        <div className="font-mono font-semibold text-sm text-stone-900">{u.usn_code}</div>
                        <div className="text-xs text-stone-500">From bundle: {u.bundle_code}</div>
                      </div>
                      {u.already_reversed && (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-300">already reversed</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 space-y-2">
            <div className="text-sm font-semibold text-stone-700">Reason for reversal *</div>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., wrong shade matched, customer rejected, packing error…"
              rows={3}
              data-testid="combo-v2-reversal-reason"
            />
          </div>

          <div className="mt-4 flex gap-2">
            <Button
              onClick={handleConfirm}
              disabled={confirming || reason.trim().length < 3}
              className="bg-fuchsia-600 hover:bg-fuchsia-700"
              data-testid="combo-v2-reversal-confirm-btn"
            >
              {confirming ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RotateCcw className="h-4 w-4 mr-1" />}
              Confirm Reversal
            </Button>
            <Button variant="outline" onClick={() => { setPreview(null); setReason(''); }}>
              Cancel
            </Button>
          </div>

          <Alert className="mt-3 bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-xs">
              On confirm: combo marked <b>REVERSED</b>, component USNs flagged
              for re-assembly (PP station will accept them again), and pieces
              pushed to the <b>Loose USN Queue</b> tab for re-bundling.
            </AlertDescription>
          </Alert>
        </Card>
      )}
    </div>
  );
}

// --------------------------------------------------------------------
// Tab 2 — Loose USN queue (wrap into fresh bundle)
// --------------------------------------------------------------------
function LooseQueueTab() {
  const [rows, setRows] = useState([]);
  const [bySku, setBySku] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [bundleCode, setBundleCode] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/combo-v2/reversal/loose-usns?status=PENDING_WRAP`,
        { headers: authHeaders() }
      );
      setRows(r.data.loose_usns || []);
      setBySku(r.data.by_sku || []);
      setSelected({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectedUsns = rows.filter(r => selected[r.usn_code]);
  const selectedSkus = [...new Set(selectedUsns.map(r => r.sku_code))];

  const handleWrap = async () => {
    if (selectedUsns.length === 0) { toast.error('Select at least one USN'); return; }
    if (selectedSkus.length > 1) { toast.error('All selected USNs must share the same SKU'); return; }
    if (!bundleCode.trim()) { toast.error('Bundle code required'); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/combo-v2/reversal/loose-usns/wrap-bundle`,
        {
          bundle_code: bundleCode.trim().toUpperCase(),
          usn_codes: selectedUsns.map(u => u.usn_code),
        },
        { headers: authHeaders() }
      );
      toast.success(`Bundle ${r.data.bundle_code} created · ${r.data.wrapped_count} pcs · PENDING_PUT_AWAY`);
      setBundleCode('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to wrap bundle');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (usn) => setSelected(prev => ({ ...prev, [usn]: !prev[usn] }));

  const selectAllOfSku = (sku) => {
    const next = { ...selected };
    rows.filter(r => r.sku_code === sku).forEach(r => { next[r.usn_code] = true; });
    setSelected(next);
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="font-semibold text-stone-900">Loose Pieces Queue</h3>
            <p className="text-xs text-stone-500">USNs released by V2 Reversal awaiting re-bundling</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="combo-v2-reversal-queue-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
          {bySku.map(g => (
            <Card key={g.sku_code} className="p-2 cursor-pointer hover:bg-stone-50" onClick={() => selectAllOfSku(g.sku_code)}>
              <div className="text-xs font-mono">{g.sku_code}</div>
              <div className="text-sm font-semibold">{g.component_name}</div>
              <div className="text-xs text-stone-500">{g.design_name} {g.shade} {g.size}</div>
              <div className="text-xs text-emerald-700 mt-1">{g.pcs} pcs</div>
            </Card>
          ))}
          {bySku.length === 0 && !loading && (
            <div className="text-sm text-stone-500 col-span-3 text-center py-6">
              No pending pieces. The queue is empty.
            </div>
          )}
        </div>

        {rows.length > 0 && (
          <div className="border border-stone-200 rounded">
            <table className="w-full text-xs">
              <thead className="bg-stone-100 text-stone-600">
                <tr>
                  <th className="p-2 text-left">✓</th>
                  <th className="p-2 text-left">USN</th>
                  <th className="p-2 text-left">SKU</th>
                  <th className="p-2 text-left">Component</th>
                  <th className="p-2 text-left">Design</th>
                  <th className="p-2 text-left">Reversal</th>
                  <th className="p-2 text-left">Queued</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr
                    key={r.loose_id}
                    className={`border-t border-stone-100 cursor-pointer ${selected[r.usn_code] ? 'bg-fuchsia-50' : ''}`}
                    onClick={() => toggle(r.usn_code)}
                    data-testid={`loose-row-${r.usn_code}`}
                  >
                    <td className="p-2">
                      <input type="checkbox" checked={!!selected[r.usn_code]} readOnly />
                    </td>
                    <td className="p-2 font-mono">{r.usn_code}</td>
                    <td className="p-2 font-mono">{r.sku_code}</td>
                    <td className="p-2">{r.component_name}</td>
                    <td className="p-2">{r.design_name} {r.shade} {r.size}</td>
                    <td className="p-2 font-mono text-stone-500">{r.reversal_id}</td>
                    <td className="p-2 text-stone-500">{(r.queued_at || '').slice(0, 16).replace('T', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows.length > 0 && (
          <div className="mt-3 flex items-end gap-2">
            <div className="flex-1">
              <div className="text-xs text-stone-600 mb-1">
                Selected: {selectedUsns.length} pcs
                {selectedSkus.length > 1 && (
                  <span className="text-rose-600 ml-2 font-semibold">⚠ multiple SKUs — wrap one SKU at a time</span>
                )}
              </div>
              <Input
                value={bundleCode}
                onChange={(e) => setBundleCode(e.target.value)}
                placeholder="Scan / paste fresh bundle code (BUN######/YY-YY)"
                className="font-mono"
                data-testid="combo-v2-reversal-wrap-bundle-input"
              />
            </div>
            <Button
              onClick={handleWrap}
              disabled={busy || selectedUsns.length === 0 || selectedSkus.length > 1 || !bundleCode.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="combo-v2-reversal-wrap-bundle-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Package className="h-4 w-4 mr-1" />}
              Wrap into Bundle
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

// --------------------------------------------------------------------
// Tab 3 — History
// --------------------------------------------------------------------
function HistoryTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/combo-v2/reversal/history?limit=100`,
        { headers: authHeaders() }
      );
      setRows(r.data.reversals || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-stone-900">Recent V2 Reversals</h3>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-stone-500 text-center py-6">No reversals yet.</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-stone-100 text-stone-600">
            <tr>
              <th className="p-2 text-left">Reversal ID</th>
              <th className="p-2 text-left">Combo USN</th>
              <th className="p-2 text-left">Combo</th>
              <th className="p-2 text-left">Components</th>
              <th className="p-2 text-left">Reason</th>
              <th className="p-2 text-left">Reversed By</th>
              <th className="p-2 text-left">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.reversal_id} className="border-t border-stone-100" data-testid={`history-row-${r.reversal_id}`}>
                <td className="p-2 font-mono">{r.reversal_id}</td>
                <td className="p-2 font-mono">{r.combo_usn}</td>
                <td className="p-2">{r.combo_name || r.combo_barcode}</td>
                <td className="p-2">{(r.components || []).length}</td>
                <td className="p-2">{r.reason}</td>
                <td className="p-2 text-stone-500">{r.reversed_by}</td>
                <td className="p-2 text-stone-500">{(r.reversed_at || '').slice(0, 16).replace('T', ' ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// --------------------------------------------------------------------
// Page shell
// --------------------------------------------------------------------
export default function ComboV2Reversal() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FFFBF5] p-6" data-testid="combo-v2-reversal-page">
      <div className="max-w-[1100px] mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/wms/combo-v2')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
              <RotateCcw className="h-5 w-5" /> Combo Reversal (V2)
            </h1>
            <p className="text-xs text-stone-500 mt-1">
              Disassemble an assembled combo back into components and queue them for re-bundling.
            </p>
          </div>
        </div>

        <Tabs defaultValue="reverse" className="w-full">
          <TabsList>
            <TabsTrigger value="reverse" data-testid="tab-reverse">
              <RotateCcw className="h-4 w-4 mr-1" /> Reverse Combo
            </TabsTrigger>
            <TabsTrigger value="queue" data-testid="tab-queue">
              <Package className="h-4 w-4 mr-1" /> Loose USN Queue
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <History className="h-4 w-4 mr-1" /> History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reverse" className="mt-4">
            <ReverseTab />
          </TabsContent>
          <TabsContent value="queue" className="mt-4">
            <LooseQueueTab />
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <HistoryTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
